import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Eye, Users, TrendingUp, Heart, Play, MessageSquare,
  Share2, Clock, Trophy, ArrowUp, ArrowDown, Minus,
  Loader2, RefreshCw, BarChart3, Activity
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

export function CreatorAnalyticsDashboard() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    if (token) fetchAnalytics();
  }, [token]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/analytics/creator/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error('Failed to load analytics');
      }
    } catch {
      toast.error('Analytics unavailable');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodData = (section) => {
    if (!data) return 0;
    const map = { '24h': 'last_24h', '7d': 'last_7d', '30d': 'last_30d' };
    const key = map[period];
    if (section === 'visits') return data.profile_visits?.[key] || 0;
    if (section === 'requests') return data.requests?.[key] || 0;
    return data.engagement?.[key] || {};
  };

  const getFollowerGrowth = () => {
    if (!data) return 0;
    return period === '30d' ? data.followers?.growth_30d : data.followers?.growth_7d;
  };

  const TrendIcon = ({ value }) => {
    if (value > 0) return <ArrowUp className="w-3.5 h-3.5 text-green-400" />;
    if (value < 0) return <ArrowDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const engagement = getPeriodData('engagement');
  const chartPos = data.chart_position || {};
  const score = data.score || {};
  const trend = data.daily_trend || [];
  const maxTrendVal = Math.max(...trend.map(d => d.profile_views + d.plays + d.likes + d.new_followers), 1);

  return (
    <div className="space-y-6" data-testid="creator-analytics-dashboard">
      {/* Period Selector + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-card rounded-lg p-1 border border-border">
          {['24h', '7d', '30d'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                period === p ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`period-${p}`}
            >
              {p === '24h' ? '24 Hours' : p === '7d' ? '7 Days' : '30 Days'}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAnalytics} data-testid="refresh-analytics">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={<Eye className="w-4 h-4" />}
          label="Profile Visits"
          value={getPeriodData('visits')}
          accent="text-sky-400"
          bg="bg-sky-500/10 border-sky-500/20"
          testId="metric-visits"
        />
        <MetricCard
          icon={<Users className="w-4 h-4" />}
          label="Followers"
          value={data.followers?.current || 0}
          sub={<span className="flex items-center gap-0.5"><TrendIcon value={getFollowerGrowth()} /> {getFollowerGrowth() > 0 ? '+' : ''}{getFollowerGrowth()}</span>}
          accent="text-purple-400"
          bg="bg-purple-500/10 border-purple-500/20"
          testId="metric-followers"
        />
        <MetricCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Requests"
          value={getPeriodData('requests')}
          accent="text-orange-400"
          bg="bg-orange-500/10 border-orange-500/20"
          testId="metric-requests"
        />
        <MetricCard
          icon={<Activity className="w-4 h-4" />}
          label="Engagement Score"
          value={score.last_7d?.toFixed(0) || 0}
          sub={<span className="text-[10px]">{score.role || 'creator'}</span>}
          accent="text-emerald-400"
          bg="bg-emerald-500/10 border-emerald-500/20"
          testId="metric-score"
        />
      </div>

      {/* Chart Position + Engagement Breakdown */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Chart Position Card */}
        <Card className="bg-card border-border p-5" data-testid="chart-position-card">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wide">Chart Position</h3>
          </div>
          {chartPos.in_top_20 ? (
            <div className="text-center py-2">
              <p className="text-5xl font-bold text-yellow-400">#{chartPos.current_rank}</p>
              <p className="text-xs text-muted-foreground mt-2">Top {chartPos.role}s Chart</p>
              {chartPos.movement !== 0 && (
                <div className={`flex items-center justify-center gap-1 mt-2 text-sm ${chartPos.movement > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <TrendIcon value={chartPos.movement} />
                  {Math.abs(chartPos.movement)} positions
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground text-sm">Not in Top 20 yet</p>
              <p className="text-[10px] text-muted-foreground/80 mt-1">Keep engaging to climb the charts</p>
            </div>
          )}
        </Card>

        {/* Engagement Breakdown */}
        <Card className="bg-card border-border p-5 lg:col-span-2" data-testid="engagement-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wide">Engagement Breakdown</h3>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            <EngagementStat icon={<Play className="w-3.5 h-3.5" />} label="Plays" value={engagement.plays || 0} color="text-green-400" />
            <EngagementStat icon={<Eye className="w-3.5 h-3.5" />} label="Views" value={engagement.views || 0} color="text-sky-400" />
            <EngagementStat icon={<Heart className="w-3.5 h-3.5" />} label="Likes" value={engagement.likes || 0} color="text-red-400" />
            <EngagementStat icon={<MessageSquare className="w-3.5 h-3.5" />} label="Comments" value={engagement.comments || 0} color="text-amber-400" />
            <EngagementStat icon={<Share2 className="w-3.5 h-3.5" />} label="Shares" value={engagement.shares || 0} color="text-purple-400" />
            <EngagementStat icon={<Clock className="w-3.5 h-3.5" />} label="Watch Min" value={engagement.watch_minutes || 0} color="text-teal-400" />
          </div>
        </Card>
      </div>

      {/* 7-Day Activity Trend */}
      {trend.length > 0 && (
        <Card className="bg-card border-border p-5" data-testid="daily-trend-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wide">7-Day Activity</h3>
          </div>
          {/* Legend */}
          <div className="flex gap-4 mb-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block" /> Views</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Plays</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Likes</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400 inline-block" /> Followers</span>
          </div>
          {/* Bar Chart */}
          <div className="flex items-end gap-2 h-32">
            {trend.map((day, i) => {
              const total = day.profile_views + day.plays + day.likes + day.new_followers;
              const h = maxTrendVal > 0 ? (total / maxTrendVal) * 100 : 0;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group relative" data-testid={`trend-bar-${i}`}>
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 bg-card border border-border rounded-lg p-2 text-[10px] hidden group-hover:block z-10 whitespace-nowrap">
                    <p className="font-bold text-foreground mb-1">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    <p className="text-sky-400">Views: {day.profile_views}</p>
                    <p className="text-green-400">Plays: {day.plays}</p>
                    <p className="text-red-400">Likes: {day.likes}</p>
                    <p className="text-purple-400">Followers: +{day.new_followers}</p>
                  </div>
                  <div
                    className="w-full rounded-t-sm bg-gradient-to-t from-primary/60 to-primary/20 transition-all duration-300 hover:from-primary/80 hover:to-primary/40 min-h-[2px]"
                    style={{ height: `${Math.max(h, 2)}%` }}
                  />
                  <span className="text-[9px] text-muted-foreground">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, sub, accent, bg, testId }) {
  return (
    <Card className={`${bg} border p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={accent}>{icon}</span>
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${accent}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function EngagementStat({ icon, label, value, color }) {
  return (
    <div className="text-center">
      <div className={`flex items-center justify-center mb-1 ${color}`}>{icon}</div>
      <p className="text-lg font-bold">{value.toLocaleString()}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
